import { defineMessages } from 'react-intl';
import { addStyling } from '@plone/volto/helpers/Extensions/withBlockSchemaEnhancer';
import type { ConfigType } from '@plone/registry';
import type { IntlShape } from 'react-intl';

type Fieldset = { id: string; fields: string[] };
type Schema = {
  fieldsets?: Fieldset[];
  properties?: Record<string, any>;
  required?: string[];
};
type SchemaEnhancerArgs = {
  schema: Schema;
  intl: IntlShape;
  [key: string]: unknown;
};
type SchemaEnhancer = (args: SchemaEnhancerArgs) => Schema;

const messages = defineMessages({
  largura: { id: 'mpf_largura', defaultMessage: 'Largura' },
  larguraHelp: {
    id: 'mpf_largura_help',
    defaultMessage:
      'Blocos vizinhos que somam a linha inteira ficam lado a lado. No celular, todos empilham.',
  },
  total: { id: 'mpf_largura_total', defaultMessage: 'Página inteira' },
  doisTercos: { id: 'mpf_largura_dois_tercos', defaultMessage: 'Dois terços' },
  metade: { id: 'mpf_largura_metade', defaultMessage: 'Metade' },
  umTerco: { id: 'mpf_largura_um_terco', defaultMessage: 'Um terço' },
});

/** Prefixo dos blocos do portal. Blocos do core e de terceiros ficam de fora. */
const PREFIXO = 'mpf';

export const LARGURA_PADRAO = 'total';

/**
 * As quatro larguras do desenho, em doze colunas: 12, 8, 6 e 4.
 *
 * O valor gravado vira a classe `has--largura--<valor>` pelo conversor padrão
 * do Volto (`helpers/Blocks/Blocks.js#buildStyleClassNamesFromData`), e é essa
 * classe que o CSS de `_internal-view.scss` traduz em `col-span`.
 */
export const LARGURAS = ['total', 'dois-tercos', 'metade', 'um-terco'] as const;

const addLarguraField = (schema: Schema, intl: IntlShape): Schema => {
  addStyling({ schema, intl } as any);

  const styles = schema?.properties?.styles?.schema;
  if (!styles?.properties || styles.properties.largura) return schema;

  styles.properties.largura = {
    title: intl.formatMessage(messages.largura),
    description: intl.formatMessage(messages.larguraHelp),
    choices: [
      ['total', intl.formatMessage(messages.total)],
      ['dois-tercos', intl.formatMessage(messages.doisTercos)],
      ['metade', intl.formatMessage(messages.metade)],
      ['um-terco', intl.formatMessage(messages.umTerco)],
    ],
    default: LARGURA_PADRAO,
    // Sem a opção vazia: "sem largura" não é um estado com significado — o
    // padrão é ocupar a linha inteira.
    noValueOption: false,
  };

  const fieldset = styles.fieldsets?.[0];
  if (fieldset && !fieldset.fields.includes('largura')) {
    fieldset.fields = [...fieldset.fields, 'largura'];
  }

  return schema;
};

/**
 * Acrescenta o campo "Largura" à aba de estilos de todo bloco do portal.
 *
 * Antes disto, a única forma de dois blocos ficarem lado a lado era o layout
 * fixo da capa (`View/CapaView/index.tsx`), escrito à mão em `col-span-8` e
 * `col-span-4` — inalcançável para uma página comum, que empilha tudo em
 * largura total.
 *
 * O campo mora em `styles` (o Style Wrapper do Volto) e não num campo solto do
 * bloco por um motivo prático: é o `styles` que o core já converte em classe
 * nos DOIS contextos — no editor, direto no `.block-editor-*`
 * (`EditBlockWrapper.jsx`), e na leitura, pelo wrapper de
 * `RenderBlocksWithoutTitle`. Um campo próprio exigiria repetir essa fiação.
 *
 * O `schemaEnhancer` que o bloco já tiver é PRESERVADO: acrescentamos ao
 * resultado dele, não o substituímos (mesma composição de `tabs.ts`).
 */
export const updateLarguraConfig = (config: ConfigType) => {
  const blocksConfig = config.blocks.blocksConfig as Record<string, any>;

  Object.keys(blocksConfig)
    .filter((id) => id.startsWith(PREFIXO))
    .forEach((id) => {
      const base: SchemaEnhancer | undefined = blocksConfig[id].schemaEnhancer;

      blocksConfig[id].schemaEnhancer = (args: SchemaEnhancerArgs) =>
        addLarguraField(base ? base(args) : args.schema, args.intl);
    });

  return config;
};

export default updateLarguraConfig;
